/**
 * #5068 — the hand-drawn look. A line is resampled and nudged sideways by smooth, seeded noise, then drawn twice (a
 * firm pass and a faint second pass), with slight overshoot at open ends. The seed comes from the part id and stroke
 * index (D5), so a stroke wobbles the same way in every room view, on the TV and after a reload.
 */
import type { Point } from './sketchKit';

/** A small, fast, deterministic generator (mulberry32). */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export function seedOf(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  return hash >>> 0;
}

/** Points no further apart than `step`, keeping the originals. */
function resample(points: Point[], closed: boolean, step: number): Point[] {
  const source = closed ? [...points, points[0]] : points, out: Point[] = [];
  for (let i = 0; i < source.length - 1; i++) {
    const [ax, ay] = source[i], [bx, by] = source[i + 1], parts = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / step));
    for (let k = 0; k < parts; k++) out.push([ax + (bx - ax) * k / parts, ay + (by - ay) * k / parts]);
  }
  if (!closed) out.push(source.at(-1)!);
  return out;
}

/**
 * One hand-drawn pass over `points`. `wobble` is the largest sideways nudge in sheet units. The noise is low-frequency
 * (interpolated between seeded knots), so lines bow gently instead of jittering.
 */
export function roughenStroke(points: Point[], closed: boolean, seed: number, wobble = 1.6): Point[] {
  if (points.length < 2) return points;
  // Two points cannot enclose anything: a "closed" two-point stroke is a line.
  closed = closed && points.length > 2;
  // At most about 400 samples a stroke, however long (a zig-zag across the sheet must not become tens of thousands).
  let length = 0;
  for (let i = 1; i < points.length; i++) length += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
  const random = seededRandom(seed), dense = resample(points, closed, Math.max(10, length / 400));
  const knots = Array.from({ length: Math.ceil(dense.length / 6) + 2 }, () => random() * 2 - 1);
  const noise = (i: number) => { const f = i / 6, k = Math.floor(f), t = f - k, s = t * t * (3 - 2 * t); return knots[k] * (1 - s) + knots[k + 1] * s; };
  const out = dense.map(([px, py], i): Point => {
    const [ax, ay] = dense[Math.max(0, i - 1)], [bx, by] = dense[Math.min(dense.length - 1, i + 1)];
    const length = Math.hypot(bx - ax, by - ay) || 1, nx = -(by - ay) / length, ny = (bx - ax) / length, d = noise(i) * wobble;
    return [px + nx * d, py + ny * d];
  });
  if (!closed && out.length > 1) {
    // A pen overshoots a little at each end.
    const over = (end: Point, prev: Point): Point => { const dx = end[0] - prev[0], dy = end[1] - prev[1], length = Math.hypot(dx, dy) || 1, k = wobble * .8 * random();
      return [end[0] + dx / length * k, end[1] + dy / length * k]; };
    out[0] = over(out[0], out[1]); out[out.length - 1] = over(out.at(-1)!, out.at(-2)!);
  } else if (closed) out.push(out[0]);
  return out;
}

/** SVG path data for a list of points (rounded to tenths). */
export function strokePath(points: Point[]): string {
  return points.map(([px, py], i) => `${i ? 'L' : 'M'}${Math.round(px * 10) / 10} ${Math.round(py * 10) / 10}`).join(' ');
}
